import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster as Sonner } from "@cm/components/ui/sonner";
import { Toaster } from "@cm/components/ui/toaster";
import { TooltipProvider } from "@cm/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import "./index.css";
import "./App.css";
import {
  getCommunitySession,
  getHomeRouteForRole,
  type CommunityRole,
} from "@cm/lib/auth";

// Eagerly loaded pages (landing)
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import { AdminPlatformRoutes } from "./layouts/AdminPlatformRoutes.tsx";

// Lazy loaded pages
const ContactUs = lazy(() => import("./pages/ContactUs.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const Signup = lazy(() => import("./pages/Signup.tsx"));
const MemberLoginPage = lazy(() => import("./pages/MemberLoginPage.tsx"));

// Lazy loaded layouts
const AdminLayout = lazy(() => import("./layouts/AdminLayout.tsx"));
const MemberLayout = lazy(() => import("./layouts/MemberLayout.tsx"));

// Admin pages - lazy
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const CommunityManagement = lazy(
  () => import("./pages/admin/CommunityManagement.tsx")
);
const MembershipManagement = lazy(
  () => import("./pages/admin/MembershipManagement.tsx")
);
const MemberDirectory = lazy(() => import("./pages/admin/MemberDirectory.tsx"));
const EventManagement = lazy(() => import("./pages/admin/EventManagement.tsx"));
const EventVolunteering = lazy(
  () => import("./pages/admin/EventVolunteering.tsx")
);
const EventDonations = lazy(() => import("./pages/admin/EventDonations.tsx"));
const EventSponsorship = lazy(
  () => import("./pages/admin/EventSponsorship.tsx")
);
const Donations = lazy(() => import("./pages/admin/Donations.tsx"));
const Sponsorships = lazy(() => import("./pages/admin/Sponsorships.tsx"));
const FeedAnnouncements = lazy(
  () => import("./pages/admin/FeedAnnouncements.tsx")
);
const ChatModule = lazy(() => import("./pages/admin/ChatModule.tsx"));
const Gallery = lazy(() => import("./pages/admin/Gallery.tsx"));
const SadharmikData = lazy(() => import("./pages/admin/SadharmikData.tsx"));
const BirthdayAnniversary = lazy(
  () => import("./pages/admin/BirthdayAnniversary.tsx")
);
const DharmashalaManagement = lazy(
  () => import("./pages/admin/DharmashalaManagement.tsx")
);
const BhojanshalManagement = lazy(
  () => import("./pages/admin/BhojanshalManagement.tsx")
);
const TempleDirectory = lazy(() => import("./pages/admin/TempleDirectory.tsx"));
const Collaboration = lazy(() => import("./pages/admin/Collaboration.tsx"));
const Feedback = lazy(() => import("./pages/admin/Feedback.tsx"));
const AssetManagement = lazy(() => import("./pages/admin/AssetManagement.tsx"));
const StockManagement = lazy(() => import("./pages/admin/StockManagement.tsx"));
const OnlineAuction = lazy(() => import("./pages/admin/OnlineAuction.tsx"));
const WhatsAppIntegration = lazy(
  () => import("./pages/admin/WhatsAppIntegration.tsx")
);
const BulkUpload = lazy(() => import("./pages/admin/BulkUpload.tsx"));
const Notifications = lazy(() => import("./pages/admin/Notifications.tsx"));
const ReferEarn = lazy(() => import("./pages/admin/ReferEarn.tsx"));
const ReportsAnalytics = lazy(
  () => import("./pages/admin/ReportsAnalytics.tsx")
);
const Settings = lazy(() => import("./pages/admin/Settings.tsx"));
const HostelManagement = lazy(
  () => import("./pages/admin/HostelManagement.tsx")
);
const AyambilBooking = lazy(() => import("./pages/admin/AyambilBooking.tsx"));
const PanjrapolManagement = lazy(
  () => import("./pages/admin/PanjrapolManagement.tsx")
);
const MandalManagement = lazy(
  () => import("./pages/admin/MandalManagement.tsx")
);
const MatrimonyManagement = lazy(
  () => import("./pages/admin/MatrimonyManagement.tsx")
);
const StoreManagement = lazy(() => import("./pages/admin/StoreManagement.tsx"));
const LabelPrinting = lazy(() => import("./pages/admin/LabelPrinting.tsx"));
const AdvertisementManagement = lazy(
  () => import("./pages/admin/AdvertisementManagement.tsx")
);
const HospitalManagement = lazy(
  () => import("./pages/admin/HospitalManagement.tsx")
);

// Member pages - lazy
const MemberDashboard = lazy(() => import("./pages/member/Dashboard.tsx"));
const MemberDirectoryPage = lazy(() => import("./pages/member/Directory.tsx"));
const MemberEvents = lazy(() => import("./pages/member/Events.tsx"));
const MemberVolunteering = lazy(
  () => import("./pages/member/Volunteering.tsx")
);
const MemberDonations = lazy(() => import("./pages/member/Donations.tsx"));
const MemberAnnouncements = lazy(
  () => import("./pages/member/Announcements.tsx")
);
const MemberChat = lazy(() => import("./pages/member/Chat.tsx"));
const MemberGallery = lazy(() => import("./pages/member/Gallery.tsx"));
const MemberProfile = lazy(() => import("./pages/member/Profile.tsx"));
const MembershipCard = lazy(() => import("./pages/member/MembershipCard.tsx"));
const MemberTemples = lazy(() => import("./pages/member/Temples.tsx"));
const MemberNotifications = lazy(
  () => import("./pages/member/Notifications.tsx")
);
const MemberDharmashala = lazy(() => import("./pages/member/Dharmashala.tsx"));
const MemberBhojanshala = lazy(() => import("./pages/member/Bhojanshala.tsx"));
const MemberFeedback = lazy(() => import("./pages/member/Feedback.tsx"));
const MemberReferEarn = lazy(() => import("./pages/member/ReferEarn.tsx"));
const MemberAuction = lazy(() => import("./pages/member/Auction.tsx"));
const MemberBirthdays = lazy(() => import("./pages/member/Birthdays.tsx"));
const MemberHostel = lazy(() => import("./pages/member/Hostel.tsx"));
const MemberAyambilBooking = lazy(
  () => import("./pages/member/AyambilBooking.tsx")
);
const MemberPanjrapol = lazy(() => import("./pages/member/Panjrapol.tsx"));
const MemberMandals = lazy(() => import("./pages/member/Mandals.tsx"));
const MemberMatrimony = lazy(() => import("./pages/member/Matrimony.tsx"));
const MemberStores = lazy(() => import("./pages/member/Stores.tsx"));
const MemberHospital = lazy(() => import("./pages/member/Hospital.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

let preloadRoutesCalled = false;
const preloadRoutes = () => {
  if (preloadRoutesCalled) {
    return;
  }
  preloadRoutesCalled = true;
  const batches = [
    [
      () => import("./layouts/AdminLayout.tsx"),
      () => import("./layouts/MemberLayout.tsx"),
      () => import("./pages/member/Dashboard.tsx"),
      () => import("./pages/admin/Dashboard.tsx"),
    ],
    [
      () => import("./pages/member/Directory.tsx"),
      () => import("./pages/member/Events.tsx"),
      () => import("./pages/member/Donations.tsx"),
      () => import("./pages/member/Profile.tsx"),
      () => import("./pages/member/Chat.tsx"),
    ],
  ];

  let i = 0;
  const loadNext = () => {
    if (i >= batches.length) {
      return;
    }
    batches[i].forEach(r => r());
    i++;
    setTimeout(loadNext, 1500);
  };
  loadNext();
};

type CommunityEntry = "admin" | "member" | "login" | "signup" | "member-login";

interface CommunityAppProps {
  basename?: string;
}

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const RoleProtectedRoute = ({
  role,
  children,
}: {
  role: CommunityRole;
  children: JSX.Element;
}) => {
  const location = useLocation();
  const session = getCommunitySession();

  if (!session?.isAuthenticated) {
    const redirect = encodeURIComponent(
      `${location.pathname}${location.search}`
    );
    const loginPath = role === "admin" ? "/adminlogin" : "/memberlogin";
    return <Navigate to={`${loginPath}?redirect=${redirect}`} replace />;
  }

  if (session.role !== role) {
    return <Navigate to={getHomeRouteForRole(session.role)} replace />;
  }

  return children;
};

const PublicAuthRoute = ({
  variant,
}: {
  variant: "admin" | "signup" | "member";
}) => {
  const session = getCommunitySession();

  if (session?.isAuthenticated) {
    return <Navigate to={getHomeRouteForRole(session.role)} replace />;
  }

  if (variant === "admin") {
    return <Login />;
  }
  if (variant === "member") {
    return <MemberLoginPage />;
  }
  return <Signup />;
};

export const CommunityManagementRoutes = ({
  entry,
}: {
  entry?: CommunityEntry;
}) => {
  if (entry === "login") {
    return (
      <Suspense fallback={<PageLoader />}>
        <PublicAuthRoute variant="admin" />
      </Suspense>
    );
  }

  if (entry === "member-login") {
    return (
      <Suspense fallback={<PageLoader />}>
        <PublicAuthRoute variant="member" />
      </Suspense>
    );
  }

  if (entry === "signup") {
    return (
      <Suspense fallback={<PageLoader />}>
        <PublicAuthRoute variant="signup" />
      </Suspense>
    );
  }

  if (entry === "admin") {
    return (
      <RoleProtectedRoute role="admin">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="community" element={<CommunityManagement />} />
              <Route path="members" element={<MembershipManagement />} />
              <Route path="directory" element={<MemberDirectory />} />
              <Route path="events" element={<EventManagement />} />
              <Route path="volunteering" element={<EventVolunteering />} />
              <Route path="event-donations" element={<EventDonations />} />
              <Route path="event-sponsorship" element={<EventSponsorship />} />
              <Route path="donations" element={<Donations />} />
              <Route path="sponsorships" element={<Sponsorships />} />
              <Route path="announcements" element={<FeedAnnouncements />} />
              <Route path="chat" element={<ChatModule />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="sadharmik" element={<SadharmikData />} />
              <Route path="birthdays" element={<BirthdayAnniversary />} />
              <Route path="dharmashala" element={<DharmashalaManagement />} />
              <Route path="bhojanshala" element={<BhojanshalManagement />} />
              <Route path="temples" element={<TempleDirectory />} />
              <Route path="collaboration" element={<Collaboration />} />
              <Route path="feedback" element={<Feedback />} />
              <Route path="assets" element={<AssetManagement />} />
              <Route path="stock" element={<StockManagement />} />
              <Route path="auction" element={<OnlineAuction />} />
              <Route path="whatsapp" element={<WhatsAppIntegration />} />
              <Route path="bulk-upload" element={<BulkUpload />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="refer-earn" element={<ReferEarn />} />
              <Route path="reports" element={<ReportsAnalytics />} />
              <Route path="settings" element={<Settings />} />
              <Route path="hostel" element={<HostelManagement />} />
              <Route path="ayambil" element={<AyambilBooking />} />
              <Route path="panjrapol" element={<PanjrapolManagement />} />
              <Route path="mandals" element={<MandalManagement />} />
              <Route path="matrimony" element={<MatrimonyManagement />} />
              <Route path="stores" element={<StoreManagement />} />
              <Route path="labels" element={<LabelPrinting />} />
              <Route
                path="advertisements"
                element={<AdvertisementManagement />}
              />
              <Route path="hospitals" element={<HospitalManagement />} />
              <Route path="platform/*" element={<AdminPlatformRoutes />} />
            </Route>
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </Suspense>
      </RoleProtectedRoute>
    );
  }

  if (entry === "member") {
    return (
      <RoleProtectedRoute role="member">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<MemberLayout />}>
              <Route index element={<MemberDashboard />} />
              <Route path="directory" element={<MemberDirectoryPage />} />
              <Route path="events" element={<MemberEvents />} />
              <Route path="volunteering" element={<MemberVolunteering />} />
              <Route path="donations" element={<MemberDonations />} />
              <Route path="announcements" element={<MemberAnnouncements />} />
              <Route path="chat" element={<MemberChat />} />
              <Route path="gallery" element={<MemberGallery />} />
              <Route path="profile" element={<MemberProfile />} />
              <Route path="membership-card" element={<MembershipCard />} />
              <Route path="temples" element={<MemberTemples />} />
              <Route path="notifications" element={<MemberNotifications />} />
              <Route path="dharmashala" element={<MemberDharmashala />} />
              <Route path="bhojanshala" element={<MemberBhojanshala />} />
              <Route path="feedback" element={<MemberFeedback />} />
              <Route path="refer-earn" element={<MemberReferEarn />} />
              <Route path="auction" element={<MemberAuction />} />
              <Route path="birthdays" element={<MemberBirthdays />} />
              <Route path="hostel" element={<MemberHostel />} />
              <Route path="ayambil" element={<MemberAyambilBooking />} />
              <Route path="panjrapol" element={<MemberPanjrapol />} />
              <Route path="mandals" element={<MemberMandals />} />
              <Route path="matrimony" element={<MemberMatrimony />} />
              <Route path="stores" element={<MemberStores />} />
              <Route path="hospital" element={<MemberHospital />} />
            </Route>
            <Route path="*" element={<Navigate to="." replace />} />
          </Routes>
        </Suspense>
      </RoleProtectedRoute>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/adminlogin" replace />} />
        <Route path="/home" element={<Index />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        <Route
          path="/admin"
          element={
            <RoleProtectedRoute role="admin">
              <AdminLayout />
            </RoleProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="community" element={<CommunityManagement />} />
          <Route path="members" element={<MembershipManagement />} />
          <Route path="directory" element={<MemberDirectory />} />
          <Route path="events" element={<EventManagement />} />
          <Route path="volunteering" element={<EventVolunteering />} />
          <Route path="event-donations" element={<EventDonations />} />
          <Route path="event-sponsorship" element={<EventSponsorship />} />
          <Route path="donations" element={<Donations />} />
          <Route path="sponsorships" element={<Sponsorships />} />
          <Route path="announcements" element={<FeedAnnouncements />} />
          <Route path="chat" element={<ChatModule />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="sadharmik" element={<SadharmikData />} />
          <Route path="birthdays" element={<BirthdayAnniversary />} />
          <Route path="dharmashala" element={<DharmashalaManagement />} />
          <Route path="bhojanshala" element={<BhojanshalManagement />} />
          <Route path="temples" element={<TempleDirectory />} />
          <Route path="collaboration" element={<Collaboration />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="assets" element={<AssetManagement />} />
          <Route path="stock" element={<StockManagement />} />
          <Route path="auction" element={<OnlineAuction />} />
          <Route path="whatsapp" element={<WhatsAppIntegration />} />
          <Route path="bulk-upload" element={<BulkUpload />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="refer-earn" element={<ReferEarn />} />
          <Route path="reports" element={<ReportsAnalytics />} />
          <Route path="settings" element={<Settings />} />
          <Route path="hostel" element={<HostelManagement />} />
          <Route path="ayambil" element={<AyambilBooking />} />
          <Route path="panjrapol" element={<PanjrapolManagement />} />
          <Route path="mandals" element={<MandalManagement />} />
          <Route path="matrimony" element={<MatrimonyManagement />} />
          <Route path="stores" element={<StoreManagement />} />
          <Route path="labels" element={<LabelPrinting />} />
          <Route path="advertisements" element={<AdvertisementManagement />} />
          <Route path="hospitals" element={<HospitalManagement />} />
          <Route path="platform/*" element={<AdminPlatformRoutes />} />
        </Route>

        <Route
          path="/member"
          element={
            <RoleProtectedRoute role="member">
              <MemberLayout />
            </RoleProtectedRoute>
          }
        >
          <Route index element={<MemberDashboard />} />
          <Route path="directory" element={<MemberDirectoryPage />} />
          <Route path="events" element={<MemberEvents />} />
          <Route path="volunteering" element={<MemberVolunteering />} />
          <Route path="donations" element={<MemberDonations />} />
          <Route path="announcements" element={<MemberAnnouncements />} />
          <Route path="chat" element={<MemberChat />} />
          <Route path="gallery" element={<MemberGallery />} />
          <Route path="profile" element={<MemberProfile />} />
          <Route path="membership-card" element={<MembershipCard />} />
          <Route path="temples" element={<MemberTemples />} />
          <Route path="notifications" element={<MemberNotifications />} />
          <Route path="dharmashala" element={<MemberDharmashala />} />
          <Route path="bhojanshala" element={<MemberBhojanshala />} />
          <Route path="feedback" element={<MemberFeedback />} />
          <Route path="refer-earn" element={<MemberReferEarn />} />
          <Route path="auction" element={<MemberAuction />} />
          <Route path="birthdays" element={<MemberBirthdays />} />
          <Route path="hostel" element={<MemberHostel />} />
          <Route path="ayambil" element={<MemberAyambilBooking />} />
          <Route path="panjrapol" element={<MemberPanjrapol />} />
          <Route path="mandals" element={<MemberMandals />} />
          <Route path="matrimony" element={<MemberMatrimony />} />
          <Route path="stores" element={<MemberStores />} />
          <Route path="hospital" element={<MemberHospital />} />
        </Route>

        <Route
          path="/adminlogin"
          element={<PublicAuthRoute variant="admin" />}
        />
        <Route
          path="/memberlogin"
          element={<PublicAuthRoute variant="member" />}
        />
        <Route path="/signup" element={<PublicAuthRoute variant="signup" />} />
        <Route path="/login" element={<Navigate to="/adminlogin" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = ({ basename }: CommunityAppProps) => {
  useEffect(() => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(preloadRoutes);
    } else {
      setTimeout(preloadRoutes, 1000);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter basename={basename}>
          <CommunityManagementRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
